/**
 * 批次 zip：一个 zip 里装着多个 sticker 包 zip，由打包脚本或 Finder 生成。
 * 只读末尾的中央目录，再按目录里记的位置把每个包切出来，不把整个批次读进内存。
 * 脚本用「只存储」装包，切出来就是原包；Finder 会再压一遍，切出来后逐个解压。
 */

const EOCD_SIG = 0x06054b50
const CENTRAL_SIG = 0x02014b50
const LOCAL_SIG = 0x04034b50
const EOCD_SIZE = 22
const LOCAL_HEADER_SIZE = 30
const CENTRAL_HEADER_SIZE = 46
/** 中央目录结尾记录后面最多跟 65535 字节的注释，只在这一段里找它。 */
const EOCD_SEARCH_BYTES = EOCD_SIZE + 0xffff
const ZIP64_U16 = 0xffff
const ZIP64_U32 = 0xffffffff
const FLAG_ENCRYPTED = 0x1
const METHOD_STORED = 0
const METHOD_DEFLATED = 8

async function readView(file, start, end) {
  return new DataView(await file.slice(start, end).arrayBuffer())
}

function findEocd(view) {
  for (let offset = view.byteLength - EOCD_SIZE; offset >= 0; offset -= 1) {
    if (view.getUint32(offset, true) === EOCD_SIG) return offset
  }
  return -1
}

function baseName(path) {
  return String(path || '').split('/').filter(Boolean).pop() || ''
}

/** Finder 会带上 __MACOSX/ 和 ._xxx 元数据，和单包校验一样跳过。 */
function isIgnoredPath(path) {
  return String(path || '')
    .split('/')
    .filter(Boolean)
    .some((segment) => segment === '__MACOSX' || segment.startsWith('.'))
}

class Zip64Error extends Error {}

/**
 * 读出 zip 的全部条目。文件名一律按 UTF-8 解：macOS 打的 zip 不打 UTF-8 标记，但写的就是 UTF-8，
 * JSZip 和后端 java.util.zip 也都这样读。
 */
export async function readZipEntries(file) {
  const tailStart = Math.max(0, file.size - EOCD_SEARCH_BYTES)
  const tail = await readView(file, tailStart, file.size)
  const eocd = findEocd(tail)
  if (eocd < 0) {
    throw new Error('不是有效的 zip 文件')
  }
  const count = tail.getUint16(eocd + 10, true)
  const directorySize = tail.getUint32(eocd + 12, true)
  const directoryOffset = tail.getUint32(eocd + 16, true)
  if (count === ZIP64_U16 || directorySize === ZIP64_U32 || directoryOffset === ZIP64_U32) {
    throw new Zip64Error('批次 zip 超过 4GB 或包太多，请重新运行打包脚本分成多个批次')
  }

  const directory = await readView(file, directoryOffset, directoryOffset + directorySize)
  const decoder = new TextDecoder('utf-8')
  const entries = []
  let cursor = 0
  for (let index = 0; index < count; index += 1) {
    if (
      cursor + CENTRAL_HEADER_SIZE > directory.byteLength ||
      directory.getUint32(cursor, true) !== CENTRAL_SIG
    ) {
      throw new Error('zip 目录已损坏')
    }
    const nameLength = directory.getUint16(cursor + 28, true)
    const extraLength = directory.getUint16(cursor + 30, true)
    const commentLength = directory.getUint16(cursor + 32, true)
    const entry = {
      path: decoder.decode(
        new Uint8Array(directory.buffer, directory.byteOffset + cursor + CENTRAL_HEADER_SIZE, nameLength)
      ),
      flags: directory.getUint16(cursor + 8, true),
      method: directory.getUint16(cursor + 10, true),
      compressedSize: directory.getUint32(cursor + 20, true),
      size: directory.getUint32(cursor + 24, true),
      localOffset: directory.getUint32(cursor + 42, true)
    }
    if ([entry.compressedSize, entry.size, entry.localOffset].includes(ZIP64_U32)) {
      throw new Zip64Error('批次 zip 超过 4GB，请重新运行打包脚本分成多个批次')
    }
    entries.push(entry)
    cursor += CENTRAL_HEADER_SIZE + nameLength + extraLength + commentLength
  }
  return entries
}

/**
 * 判断一个 zip 是单个 sticker 包还是批次。sticker 包里只有图片和 json，
 * 所以只要里面有 .zip 就是批次；读不出来的交回单包校验去报错。
 *
 * @returns {Promise<
 *   {kind: 'pack'} |
 *   {kind: 'error', message: string} |
 *   {kind: 'batch', packs: object[], ignored: string[], duplicates: string[]}
 * >}
 */
export async function inspectBatchZip(file) {
  let entries
  try {
    entries = await readZipEntries(file)
  } catch (error) {
    if (error instanceof Zip64Error) {
      return { kind: 'error', message: error.message }
    }
    return { kind: 'pack' }
  }

  const packs = []
  const ignored = []
  const duplicates = []
  const seen = new Set()
  entries.forEach((entry) => {
    if (entry.path.endsWith('/') || isIgnoredPath(entry.path)) return
    const name = baseName(entry.path)
    if (!name.toLowerCase().endsWith('.zip')) {
      ignored.push(name)
      return
    }
    // Finder 打包时可能带着子文件夹，不同文件夹里的同名包只收第一个，否则落库后分不清。
    if (seen.has(name)) {
      duplicates.push(name)
      return
    }
    seen.add(name)
    packs.push({ ...entry, name })
  })

  if (!packs.length) {
    return { kind: 'pack' }
  }
  return { kind: 'batch', packs, ignored, duplicates }
}

async function streamToBlob(stream) {
  const reader = stream.getReader()
  const chunks = []
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
  }
  return new Blob(chunks)
}

async function packBlob(file, pack) {
  if (pack.flags & FLAG_ENCRYPTED) {
    throw new Error('已加密，无法读取')
  }
  const header = await readView(file, pack.localOffset, pack.localOffset + LOCAL_HEADER_SIZE)
  if (header.byteLength < LOCAL_HEADER_SIZE || header.getUint32(0, true) !== LOCAL_SIG) {
    throw new Error('在批次里找不到这个包，批次 zip 可能已损坏')
  }
  // 本地头里的文件名和扩展字段长度可能和中央目录不同，数据起点要按本地头算。
  const dataStart = pack.localOffset + LOCAL_HEADER_SIZE + header.getUint16(26, true) + header.getUint16(28, true)
  const data = file.slice(dataStart, dataStart + pack.compressedSize)
  if (pack.method === METHOD_STORED) {
    return data
  }
  if (pack.method === METHOD_DEFLATED) {
    return streamToBlob(data.stream().pipeThrough(new DecompressionStream('deflate-raw')))
  }
  throw new Error('压缩方式不支持，请用打包脚本重新生成批次')
}

/** 把批次里的一个包变成普通 File，后面的校验、上云、落库和直接选的 zip 完全一样。 */
export async function openBatchPack(file, pack) {
  const blob = await packBlob(file, pack)
  if (blob.size !== pack.size) {
    throw new Error('读出来的大小不对，批次 zip 可能已损坏')
  }
  return new File([blob], pack.name, { type: 'application/zip', lastModified: file.lastModified })
}
